import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Loader2, X, UserRound, Layers } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { useMe } from '../../lib/me.js'
import { canViewSalary, isHR } from '../../lib/role.js'
import { relabelBranch, indexBranches, roleList, activeInfo, fullName, isOnProbation, positionChoices } from '../../lib/employee.js'
import EmployeeEditor from '../../components/employee/EmployeeEditor.jsx'
import Pager from '../../components/Pager.jsx'
import SearchableSelect from '../../components/SearchableSelect.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'

const PER_PAGE = 30

// The view HR opens on. Changed 2026-08-19: ใช้งาน + จัดกลุ่มตามตำแหน่ง, because a roster
// padded with people who no longer work here is not the question this page answers,
// and position is how a manager reads it. Referenced in three places that must agree —
// the persisted initial values, `hasFilter`, and ล้าง — so it lives here once.
// A filter toggle. Off it reads as a neutral control; on it takes a quiet TINT, not a
// fill — the CRM's other filter bars are neutral selects plus a subtle ล้าง, and the one
// solid brand button on this page should be the primary action (+ เพิ่มพนักงาน). Two
// saturated buttons of equal weight is what made this row read as cluttered.
function FilterChip({ on, onClick, title, tone = 'brand', icon: Icon, children }) {
  const active = tone === 'amber'
    ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
    : 'text-brand-700 bg-brand-50 border-brand-200 hover:bg-brand-100'
  return (
    <button
      type="button" onClick={onClick} title={title} aria-pressed={on}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium border transition-colors ${
        on ? active : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'
      }`}
    >
      {Icon && <Icon size={14} strokeWidth={1.9} />}{children}
    </button>
  )
}

const DEFAULTS = {
  branchId: '',
  active: 'true',
  probationOnly: false,
  position: '',
  grouped: true,
}

// active filter → the GET /employee tri-state param ('' = any)
const ACTIVE_OPTS = [
  { value: '', label: 'ทุกสถานะ' },
  { value: 'true', label: 'ใช้งาน' },
  { value: 'false', label: 'ปิดใช้งาน' },
]

function ActivePill({ active }) {
  const a = activeInfo(active !== false)
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${a.tone}`}>{a.label}</span>
}

// One employee row. Extracted so the flat and grouped views cannot drift apart.
function Row({ e, relabel, onOpen }) {
  return (
    <tr
      onClick={onOpen}
      className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          {e.image
            ? <img src={e.image} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-slate-200 shrink-0" />
            : <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center shrink-0"><UserRound size={16} /></span>}
          <div className="min-w-0">
            <div className="font-medium text-slate-800 truncate">{fullName(e)}</div>
            {e.nickname && <div className="text-[12px] text-slate-400 truncate">{e.nickname}</div>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600">{e.position || '—'}</td>
      <td className="px-4 py-3 text-slate-600">{relabel(e.branch_id)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {roleList(e).slice(0, 3).map((r) => (
            <span key={r} className="inline-flex items-center rounded-md bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[11px]">{r}</span>
          ))}
          {roleList(e).length > 3 && <span className="text-[11px] text-slate-400">+{roleList(e).length - 3}</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1">
          <ActivePill active={e.active} />
          {isOnProbation(e) && (
            <span
              title={`ครบกำหนดทดลองงาน ${e.date_probationpass}`}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 bg-amber-50 text-amber-700 ring-amber-200"
            >
              ทดลองงาน · ถึง {e.date_probationpass}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}

export default function EmployeeList() {
  const me = useMe()
  const canSalary = canViewSalary(me)
  const [branches, setBranches] = useState([])
  const [positions, setPositions] = useState(null)

  // Shared with every พนักงาน tab — see EmployeeFilter.jsx. Was a free-text box until
  // 2026-10-30; a text query names no one, so it could not follow you to the next tab.
  const [employeeId, setEmployeeId] = useEmployeeFilter()
  const [branchId, setBranchId] = usePersistedState('emp:branch', DEFAULTS.branchId)
  // ⚠️ The keys carry a `2` because these two defaults CHANGED on 2026-08-19, and a
  // changed default reaches nobody on its own: usePersistedState writes on mount, so
  // every account that has ever opened this page already has the old '' / false in
  // localStorage and would keep them forever. Bumping the key is what makes the new
  // default apply — at the cost of resetting these two filters once per account,
  // which IS the intent. The other filters keep their original keys and their saved
  // values. Bump again (3, 4…) only for another deliberate default change.
  //
  // ใช้งาน by default: a roster of people who no longer work here is not the question
  // HR opens this page to answer, and ex-staff were padding every count.
  const [active, setActive] = usePersistedState('emp:active2', DEFAULTS.active)
  const [probationOnly, setProbationOnly] = usePersistedState('emp:probation', DEFAULTS.probationOnly)
  const [position, setPosition] = usePersistedState('emp:position', DEFAULTS.position)
  // จัดกลุ่มตามตำแหน่ง by default. This is only affordable because it forces fullFetch
  // (per_page=500) — which cost 2.6–5.0s per load until the slim endpoint landed the
  // same day and took it to 0.09s. If that endpoint is ever reverted, revisit this.
  const [grouped, setGrouped] = usePersistedState('emp:grouped2', DEFAULTS.grouped)
  const [page, setPage] = useState(1)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageTotal, setPageTotal] = useState(1)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editor, setEditor] = useState(null) // { mode, employeeId }
  const reqId = useRef(0)

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  // ตำแหน่ง is an HR-managed catalog (employee_position), so the filter is built from
  // it rather than from a constant — a position added in the editor has to be
  // filterable here in the same session, which is what the invalidateCache() after
  // the create is for. positionChoices() falls back to the twelve built-in names if
  // the request fails, so the filter never empties out.
  useEffect(() => {
    cachedGet('/api/hr/positions').then((r) => setPositions(Array.isArray(r) ? r : [])).catch(() => {})
  }, [])

  // The saved filter is kept in the list even if that position has since been
  // retired — otherwise the dropdown reads "ทุกตำแหน่ง" while the rows stay filtered.
  const positionOpts = useMemo(
    () => [{ value: '', label: 'ทุกตำแหน่ง' }, ...positionChoices(positions, position).map((p) => ({ value: p, label: p }))],
    [positions, position],
  )

  const branchName = useMemo(() => indexBranches(branches), [branches])

  useEffect(() => { setPage(1) }, [employeeId, branchId, active, probationOnly, position, grouped])

  // `position` IS a Xano parameter, so that filter stays server-side and paged as normal.
  //
  // Probation and grouping are not. Neither can be derived from one page: filtering the 30
  // rows the server happened to return would show "2 คน" under a footer still claiming 383,
  // and grouping them would produce headings whose counts change as you page. So either of
  // those switches the fetch to the whole matching roster in one request (~383 rows, the
  // same fetch the assignment wizard already makes) and drops server paging for that view.
  // So does a picked employee: GET /employee has no id filter, and the person is then
  // one row of the full roster — still subject to สาขา / ตำแหน่ง / สถานะ, so a picked
  // ex-employee under ใช้งาน lands on the empty state rather than slipping past it.
  const fullFetch = probationOnly || grouped || Boolean(employeeId)

  function load() {
    const id = ++reqId.current
    setLoading(true); setErr('')
    apiGet('/api/hr/employees', {
      // The roster payload (api 574) — no addons. The fat endpoint runs four
      // subqueries per row (timetable, salary_em, appointmentList, a creator
      // lookup) and this table renders none of them; at per_page=500 that cost
      // 2.6–5.0s per load. Nothing below reads a field the slim shape omits.
      slim: 1,
      page: fullFetch ? 1 : page,
      per_page: fullFetch ? 500 : PER_PAGE,
      branch_id: branchId || undefined,
      active: active || undefined,
      position: position || undefined,
    })
      .then((r) => {
        if (id !== reqId.current) return
        const items = Array.isArray(r) ? r : (r?.items || [])
        if (fullFetch) {
          const kept = items.filter((e) => (!probationOnly || isOnProbation(e))
            && (!employeeId || String(e.id) === String(employeeId)))
          setRows(kept)
          setTotal(kept.length)
          setPageTotal(1)
        } else {
          setRows(items)
          setTotal(r?.itemsTotal ?? items.length)
          setPageTotal(r?.pageTotal ?? 1)
        }
      })
      .catch((e) => { if (id === reqId.current) setErr(String(e.message || e)) })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }
  useEffect(load, [page, employeeId, branchId, active, probationOnly, position, grouped]) // eslint-disable-line react-hooks/exhaustive-deps

  // Biggest group first, same ordering rule as the assignment wizard's step 1, so the long
  // tail of one-person positions sinks below the twenty ผู้ช่วย instead of pushing them off
  // the screen.
  const groups = useMemo(() => {
    if (!grouped) return []
    const m = new Map()
    for (const e of rows) {
      const p = e.position || 'ไม่ระบุตำแหน่ง'
      if (!m.has(p)) m.set(p, [])
      m.get(p).push(e)
    }
    return [...m.entries()].sort((a, b) => (b[1].length - a[1].length) || a[0].localeCompare(b[0], 'th'))
  }, [rows, grouped])

  function refresh() { setEditor(null); load() }

  // "Is this different from the view the page opens on?" — NOT "is anything set?".
  // ใช้งาน and จัดกลุ่ม are truthy defaults, so the old truthiness test would leave ล้าง
  // on screen permanently with nothing to clear.
  const hasFilter = Boolean(employeeId) || branchId !== DEFAULTS.branchId
    || active !== DEFAULTS.active || probationOnly !== DEFAULTS.probationOnly
    || position !== DEFAULTS.position || grouped !== DEFAULTS.grouped
  const relabel = (id) => relabelBranch(branchName.get(Number(id)) || '') || '—'
  const branchOpts = useMemo(
    () => [{ value: '', label: 'ทุกสาขา' }, ...branches.map((b) => ({ value: String(b.id), label: relabelBranch(b.name) }))],
    [branches],
  )

  return (
    <div className="max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-5">
        <p className="text-[13.5px] text-slate-500">เพิ่ม/แก้ไขข้อมูลพนักงาน · กรองตามสาขาและสถานะ</p>
        <button
          onClick={() => setEditor({ mode: 'create' })}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm"
        >
          <Plus size={16} /> เพิ่มพนักงาน
        </button>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <EmployeePicker
          value={employeeId} onChange={setEmployeeId} allowAll activeOnly={false}
          className="flex-1 min-w-[220px] max-w-[320px]"
        />
        <SearchableSelect className="w-[170px] shrink-0" options={branchOpts} value={String(branchId)} onChange={setBranchId} placeholder="ทุกสาขา" />
        <SearchableSelect className="w-[170px] shrink-0" options={positionOpts} value={position} onChange={setPosition} placeholder="ทุกตำแหน่ง" />
        <SearchableSelect className="w-[140px] shrink-0" options={ACTIVE_OPTS} value={active} onChange={setActive} placeholder="ทุกสถานะ" />
        {/* View switches + reset, right-aligned — the same trailing cluster
            EmployeeClockin uses, so the two tabs of this section read alike. */}
        <div className="ml-auto flex items-center gap-2">
          <FilterChip
            on={grouped} onClick={() => setGrouped((v) => !v)} icon={Layers}
            title="จัดกลุ่มรายชื่อตามตำแหน่ง (เรียงกลุ่มใหญ่ก่อน)"
          >
            จัดกลุ่มตามตำแหน่ง
          </FilterChip>
          <FilterChip
            on={probationOnly} onClick={() => setProbationOnly((v) => !v)} tone="amber"
            title="กรองเฉพาะคนที่วันผ่านทดลองงานยังมาไม่ถึง (วันนี้หรือหลังจากนี้)"
          >
            อยู่ระหว่างทดลองงาน
          </FilterChip>
          {hasFilter && (
            <button
              onClick={() => {
                setEmployeeId(''); setBranchId(DEFAULTS.branchId); setActive(DEFAULTS.active)
                setProbationOnly(DEFAULTS.probationOnly); setPosition(DEFAULTS.position); setGrouped(DEFAULTS.grouped)
              }}
              title="กลับไปมุมมองตั้งต้น (ใช้งาน · จัดกลุ่มตามตำแหน่ง)"
              className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100"
            >
              <X size={14} /> ล้าง
            </button>
          )}
        </div>
      </div>

      {/* table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">พนักงาน</th>
                <th className="px-4 py-2.5">ตำแหน่ง</th>
                <th className="px-4 py-2.5">สาขา</th>
                <th className="px-4 py-2.5">บทบาท</th>
                <th className="px-4 py-2.5">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>
              )}
              {!loading && !err && !rows.length && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">{hasFilter ? 'ไม่พบพนักงานที่ตรงกับตัวกรอง' : 'ยังไม่มีพนักงาน — กด “เพิ่มพนักงาน” เพื่อเริ่ม'}</td></tr>
              )}
              {/* Grouped view inserts a heading row per position; the employee rows
                  themselves are identical either way. */}
              {!loading && !err && grouped && groups.map(([pos, items]) => (
                <Fragment key={pos}>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <td colSpan={5} className="px-4 py-2">
                      <span className="text-[12.5px] font-semibold text-slate-600">{pos}</span>
                      <span className="ml-2 text-[11.5px] text-slate-400 tabular-nums">{items.length} คน</span>
                    </td>
                  </tr>
                  {items.map((e) => (
                    <Row key={e.id} e={e} relabel={relabel} onOpen={() => setEditor({ mode: 'edit', employeeId: e.id })} />
                  ))}
                </Fragment>
              ))}
              {!loading && !err && !grouped && rows.map((e) => (
                <Row key={e.id} e={e} relabel={relabel} onOpen={() => setEditor({ mode: 'edit', employeeId: e.id })} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* footer / paging */}
      <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
        <span>ทั้งหมด {total.toLocaleString()} คน</span>
        <Pager page={page} pageTotal={pageTotal} onChange={setPage} />
      </div>

      {editor && (
        <EmployeeEditor
          mode={editor.mode}
          employeeId={editor.employeeId}
          branches={branches}
          canSalary={canSalary}
          isHrAdmin={isHR(me)}
          onClose={() => setEditor(null)}
          onSaved={refresh}
        />
      )}
    </div>
  )
}
